'use client';

import { useEffect, useState } from 'react';
import { ParticipantKind, type RemoteParticipant, RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import {
  TURN_EMOTION_TOPIC,
  type TurnEmotionEvent,
  type TurnEmotionRelation,
  type TurnEmotionStatus,
  turnEmotionLabels,
} from '@/types/turn-emotion';

const MAX_EVENT_BYTES = 64 * 1024;
const MAX_HISTORY = 10;
const statuses = new Set<TurnEmotionStatus>(['ready', 'insufficient', 'unavailable', 'skipped']);
const relations = new Set<TurnEmotionRelation>([
  'agree',
  'conflict',
  'single_modality',
  'conflict_with_text',
  'insufficient',
]);
const labels = new Set<string>(turnEmotionLabels);

export function isTurnEmotionPacket(topic: string | undefined, kind: ParticipantKind | undefined) {
  return topic === TURN_EMOTION_TOPIC && kind === ParticipantKind.AGENT;
}

export function parseTurnEmotionEvent(payload: Uint8Array): TurnEmotionEvent | null {
  if (payload.byteLength === 0 || payload.byteLength > MAX_EVENT_BYTES) return null;
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder().decode(payload));
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const event = value as Record<string, unknown>;
  if (
    event.schema_version !== '1' ||
    event.type !== 'turn_emotion' ||
    typeof event.turn_id !== 'string' ||
    !event.turn_id ||
    typeof event.message_id !== 'string' ||
    !event.message_id ||
    (event.input_modality !== 'audio' && event.input_modality !== 'text') ||
    !statuses.has(event.status as TurnEmotionStatus) ||
    typeof event.usable !== 'boolean' ||
    (event.relation !== null && !relations.has(event.relation as TurnEmotionRelation)) ||
    (event.overall_label !== null && !labels.has(event.overall_label as string)) ||
    !Array.isArray(event.cues) ||
    (event.reason !== null && typeof event.reason !== 'string') ||
    (event.service_latency_ms !== null &&
      (typeof event.service_latency_ms !== 'number' || event.service_latency_ms < 0)) ||
    typeof event.completed_at_ms !== 'number'
  ) {
    return null;
  }
  for (const cue of event.cues) {
    if (
      !cue ||
      typeof cue !== 'object' ||
      (cue.modality !== 'audio' && cue.modality !== 'video') ||
      !labels.has(cue.label)
    ) {
      return null;
    }
  }
  return event as unknown as TurnEmotionEvent;
}

export function mergeTurnEmotionHistory(
  history: TurnEmotionEvent[],
  event: TurnEmotionEvent
): TurnEmotionEvent[] {
  const withoutDuplicate = history.filter(
    (item) => item.turn_id !== event.turn_id && item.message_id !== event.message_id
  );
  return [event, ...withoutDuplicate].slice(0, MAX_HISTORY);
}

export function useTurnEmotion(enabled: boolean) {
  const room = useRoomContext();
  const [history, setHistory] = useState<TurnEmotionEvent[]>([]);

  useEffect(() => {
    if (!enabled) {
      setHistory([]);
      return;
    }
    const onData = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      _kind?: unknown,
      topic?: string
    ) => {
      if (!isTurnEmotionPacket(topic, participant?.kind)) return;
      const event = parseTurnEmotionEvent(payload);
      if (event) setHistory((current) => mergeTurnEmotionHistory(current, event));
    };
    const clear = () => setHistory([]);
    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.Disconnected, clear);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.Disconnected, clear);
      clear();
    };
  }, [enabled, room]);

  return { latest: history[0], history };
}
